import React, { useState } from 'react';
import { ShoppingBag, Star, ShieldCheck, Truck, RefreshCw, CheckCircle, Eye } from 'lucide-react';
import { resolveMediaUrl, handleImageError } from '../../../utils/mediaResolver';

export default function ProductPreview({ form = {}, categoryOptions = [] }) {
  const [activeTab, setActiveTab] = useState('detail'); // 'detail' | 'card'
  const [selectedPhoto, setSelectedPhoto] = useState(null);

  const galleryList = Array.isArray(form.gallery)
    ? form.gallery.filter(Boolean)
    : (typeof form.gallery === 'string' && form.gallery ? form.gallery.split(',').map((s) => s.trim()).filter(Boolean) : []);

  const mainPhoto = selectedPhoto || form.featured_image || galleryList[0] || '';
  const websiteCategory = categoryOptions.find((c) => String(c.id) === String(form.website_category_id))?.name || form.category || 'Refurbished Device';

  const regularPrice = Number(form.regular_price) || 0;
  const salePrice = Number(form.sale_price) || 0;
  const discountPct = regularPrice > salePrice && regularPrice > 0
    ? Math.round(((regularPrice - salePrice) / regularPrice) * 100)
    : 0;

  const stockCount = Number(form.stock ?? form.stock_quantity ?? 0);
  const status = form.website_status || 'draft';

  // Parse specifications
  let specsObj = {};
  if (form.specifications) {
    if (typeof form.specifications === 'object') {
      specsObj = form.specifications;
    } else if (typeof form.specifications === 'string') {
      try {
        specsObj = JSON.parse(form.specifications);
      } catch {
        specsObj = {};
      }
    }
  }

  const colorsList = typeof form.colors === 'string'
    ? form.colors.split(',').map((c) => c.trim()).filter(Boolean)
    : Array.isArray(form.colors) ? form.colors : [];

  return (
    <div className="bg-slate-50 min-h-full flex flex-col font-sans text-gray-900">
      {/* Top Preview Sub-bar */}
      <div className="bg-white border-b px-4 py-2 flex flex-wrap items-center justify-between gap-2 text-xs">
        <div className="flex items-center gap-2">
          <span className="font-semibold text-gray-500">View as:</span>
          <div className="flex bg-slate-100 p-0.5 rounded-lg border">
            <button
              type="button"
              onClick={() => setActiveTab('detail')}
              className={`px-3 py-1 rounded-md text-xs font-semibold transition ${
                activeTab === 'detail' ? 'bg-white text-[#2B3380] shadow-sm' : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              🛍️ Storefront Page
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('card')}
              className={`px-3 py-1 rounded-md text-xs font-semibold transition ${
                activeTab === 'card' ? 'bg-white text-[#2B3380] shadow-sm' : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              🏷️ Catalog Card
            </button>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <span
            className={`px-2 py-0.5 rounded-full font-bold text-[10px] uppercase ${
              status === 'published'
                ? 'bg-green-100 text-green-800'
                : status === 'hidden'
                ? 'bg-red-100 text-red-800'
                : 'bg-amber-100 text-amber-800'
            }`}
          >
            {status === 'published' ? '● Live on Storefront' : status === 'hidden' ? '○ Hidden' : '◐ Draft (Offline)'}
          </span>
          <span className="text-gray-400 text-[11px]">Order: #{form.display_order ?? 0}</span>
        </div>
      </div>

      {activeTab === 'card' ? (
        /* Catalog Card Mockup */
        <div className="p-8 flex justify-center items-center flex-1">
          <div className="w-72 bg-white rounded-2xl border border-slate-200 shadow-lg overflow-hidden hover:shadow-xl transition flex flex-col">
            <div className="h-56 bg-slate-100 relative p-4 flex items-center justify-center overflow-hidden">
              {discountPct > 0 && (
                <span className="absolute top-3 left-3 bg-red-600 text-white text-[11px] font-bold px-2 py-0.5 rounded-md shadow-sm">
                  {discountPct}% OFF
                </span>
              )}
              <span className="absolute top-3 right-3 bg-slate-900/80 text-white text-[10px] font-semibold px-2 py-0.5 rounded-full">
                {form.brand || 'Refurbished'}
              </span>
              {mainPhoto ? (
                <img
                  src={resolveMediaUrl(mainPhoto)}
                  alt={form.name || 'Product'}
                  className="max-h-full max-w-full object-contain"
                  onError={handleImageError}
                />
              ) : (
                <div className="text-gray-400 text-xs italic">No Photo Uploaded</div>
              )}
            </div>

            <div className="p-4 flex-1 flex flex-col justify-between">
              <div>
                <span className="text-[10px] font-bold uppercase tracking-wider text-[#2B3380]">
                  {websiteCategory}
                </span>
                <h3 className="font-bold text-gray-900 text-sm mt-0.5 line-clamp-2">
                  {form.name || <span className="text-gray-300 italic">Untitled Product</span>}
                </h3>
                {form.short_description && (
                  <p className="text-xs text-gray-500 mt-1 line-clamp-2">{form.short_description}</p>
                )}
              </div>

              <div className="mt-4 pt-3 border-t">
                <div className="flex items-baseline gap-2">
                  <span className="text-lg font-extrabold text-green-700">
                    ₹{salePrice.toLocaleString()}
                  </span>
                  {regularPrice > salePrice && (
                    <span className="text-xs text-gray-400 line-through">
                      ₹{regularPrice.toLocaleString()}
                    </span>
                  )}
                </div>
                <div className="mt-2 flex items-center justify-between text-[11px]">
                  <span className={stockCount > 0 ? 'text-emerald-700 font-semibold' : 'text-red-600 font-semibold'}>
                    {stockCount > 0 ? `In Stock (${stockCount})` : 'Out of Stock'}
                  </span>
                  <span className="px-2 py-0.5 rounded bg-indigo-50 text-indigo-800 font-semibold">
                    Grade: {form.variants?.[0]?.sales_grade || 'GOOD'}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        /* Storefront Product Detail Page Mockup */
        <div className="p-6 md:p-8 max-w-4xl mx-auto w-full space-y-8 flex-1">
          {/* Main Product Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 bg-white p-6 rounded-2xl border border-slate-200 shadow-sm">
            {/* Gallery Column */}
            <div className="space-y-4">
              <div className="h-72 md:h-80 bg-slate-50 border rounded-xl flex items-center justify-center p-4 relative">
                {discountPct > 0 && (
                  <span className="absolute top-3 left-3 bg-red-600 text-white text-xs font-bold px-2.5 py-1 rounded-lg shadow-sm">
                    {discountPct}% OFF
                  </span>
                )}
                {mainPhoto ? (
                  <img
                    src={resolveMediaUrl(mainPhoto)}
                    alt={form.name || 'Product'}
                    className="max-h-full max-w-full object-contain"
                    onError={handleImageError}
                  />
                ) : (
                  <div className="text-center text-gray-400 text-sm">
                    <p>No Photo Available</p>
                    <span className="text-xs text-gray-400">Upload photos in the editor</span>
                  </div>
                )}
              </div>

              {/* Thumbnails */}
              {galleryList.length > 1 && (
                <div className="flex gap-2 overflow-x-auto pb-2">
                  {galleryList.map((url, idx) => (
                    <button
                      key={idx}
                      type="button"
                      onClick={() => setSelectedPhoto(url)}
                      className={`w-16 h-16 rounded-lg border-2 p-1 bg-slate-50 flex-shrink-0 transition ${
                        mainPhoto === url ? 'border-[#2B3380] shadow-sm' : 'border-slate-200 hover:border-slate-400'
                      }`}
                    >
                      <img src={resolveMediaUrl(url)} alt="" className="w-full h-full object-contain" onError={handleImageError} />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Product Details Column */}
            <div className="space-y-4">
              <div>
                <div className="flex items-center gap-2 text-xs font-semibold text-slate-500">
                  <span>{form.brand || 'Brand'}</span>
                  <span>•</span>
                  <span>{websiteCategory}</span>
                  {form.sku_code && (
                    <>
                      <span>•</span>
                      <span className="font-mono bg-slate-100 px-1.5 py-0.5 rounded">SKU: {form.sku_code}</span>
                    </>
                  )}
                </div>
                <h1 className="text-xl md:text-2xl font-bold text-gray-900 mt-1">
                  {form.name || <span className="text-gray-300 italic">Untitled Product</span>}
                </h1>
              </div>

              {/* Price Row */}
              <div className="bg-slate-50 p-4 rounded-xl border flex items-baseline gap-3">
                <span className="text-3xl font-extrabold text-green-700">
                  ₹{salePrice.toLocaleString()}
                </span>
                {regularPrice > salePrice && (
                  <>
                    <span className="text-sm text-gray-400 line-through">
                      MRP: ₹{regularPrice.toLocaleString()}
                    </span>
                    <span className="text-xs font-bold text-red-600 bg-red-100 px-2 py-0.5 rounded">
                      Save ₹{(regularPrice - salePrice).toLocaleString()}
                    </span>
                  </>
                )}
              </div>

              {/* Badges / Guarantees */}
              <div className="grid grid-cols-2 gap-2 text-xs text-slate-600 py-2 border-y">
                <div className="flex items-center gap-1.5">
                  <ShieldCheck className="w-4 h-4 text-emerald-600" />
                  <span>ZOBOX Certified Quality</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <Truck className="w-4 h-4 text-blue-600" />
                  <span>Free Express Delivery</span>
                </div>
              </div>

              {/* Colors */}
              {colorsList.length > 0 && (
                <div>
                  <span className="text-xs font-bold text-slate-700 block mb-1.5">Available Colors:</span>
                  <div className="flex flex-wrap gap-2">
                    {colorsList.map((col, idx) => (
                      <span key={idx} className="px-2.5 py-1 rounded-md border text-xs bg-white text-slate-700 font-medium">
                        {col}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Short Description */}
              {form.short_description && (
                <p className="text-xs text-gray-600 leading-relaxed bg-blue-50/50 p-3 rounded-lg border border-blue-100">
                  {form.short_description}
                </p>
              )}

              {/* Buy Mockup Button */}
              <div className="pt-2">
                <button
                  type="button"
                  disabled
                  className="w-full py-3 rounded-xl bg-[#2B3380] text-white font-bold text-sm shadow-md flex items-center justify-center gap-2 opacity-95 cursor-default"
                >
                  <ShoppingBag className="w-4 h-4" />
                  Add to Cart • ₹{salePrice.toLocaleString()}
                </button>
              </div>
            </div>
          </div>

          {/* Full Description & Specs Tabs/Sections */}
          <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm space-y-6">
            {form.description && (
              <div>
                <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider mb-2">
                  Product Overview
                </h3>
                <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-line">
                  {form.description}
                </p>
              </div>
            )}

            {Object.keys(specsObj).length > 0 && (
              <div>
                <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider mb-3">
                  Technical Specifications
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
                  {Object.entries(specsObj).map(([k, v]) => (
                    <div key={k} className="p-2.5 bg-slate-50 rounded-lg border flex justify-between">
                      <span className="text-slate-500 font-medium">{k}</span>
                      <span className="text-slate-900 font-semibold">{String(v)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Live Variants Table if present */}
            {Array.isArray(form.variants) && form.variants.length > 0 && (
              <div>
                <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider mb-3">
                  Configured Variants ({form.variants.length})
                </h3>
                <div className="overflow-x-auto border rounded-xl">
                  <table className="w-full text-xs text-left">
                    <thead className="bg-slate-100 text-slate-600 font-semibold">
                      <tr>
                        <th className="p-2.5">Variant / Memory</th>
                        <th className="p-2.5">Grade</th>
                        <th className="p-2.5">Sale Price</th>
                        <th className="p-2.5">MRP</th>
                        <th className="p-2.5">Stock</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {form.variants.map((v, i) => (
                        <tr key={v.id || i} className="hover:bg-slate-50">
                          <td className="p-2.5 font-medium">{[v.memory, v.color].filter(Boolean).join(' / ') || v.zupc_code || `Variant ${i+1}`}</td>
                          <td className="p-2.5"><span className="px-2 py-0.5 rounded bg-indigo-50 text-indigo-700 font-bold">{v.sales_grade || 'GOOD'}</span></td>
                          <td className="p-2.5 font-bold text-green-700">₹{Number(v.sale_price || 0).toLocaleString()}</td>
                          <td className="p-2.5 text-gray-400 line-through">₹{Number(v.regular_price || v.mrp || 0).toLocaleString()}</td>
                          <td className="p-2.5">{v.stock_quantity ?? v.stock ?? 0}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
